import { Controller } from "@hotwired/stimulus";

// Étude-wizard signataire section.
//
// Two responsibilities :
//   1. Tab switcher between "Choisir un contact existant" and "Créer un
//      nouveau contact". Inactive pane's inputs are disabled so they don't
//      ship at submit time.
//   2. Live duplicate detection on the inline "new contact" prenom + nom
//      fields. Hits /contacts/verify (the same endpoint the contact wizard
//      uses), and offers "Utiliser ce contact" (which silently rattaches the
//      existing contact to the étude's entreprise) and "Voir la fiche".
export default class extends Controller {
  static targets = [
    "tab", "pane",
    "prenomField", "nomField",
    "dup", "dupTitle", "dupList",
    "existingSelect",
  ];

  static values = {
    verifyUrl: String,
  };

  connect() {
    // Initial sync: disable inputs in non-active panes so they don't ship.
    this.paneTargets.forEach((p) => {
      if (p.hidden) {
        p.querySelectorAll("input, select, textarea").forEach((el) => { el.disabled = true; });
      }
    });
    this._abort = null;
    this._timer = null;
  }

  disconnect() {
    if (this._abort) this._abort.abort();
    if (this._timer) clearTimeout(this._timer);
  }

  switch(event) {
    const mode = event.currentTarget.value;
    this.tabTargets.forEach((t) => {
      t.classList.toggle("is-active", t.dataset.mode === mode);
    });
    this.paneTargets.forEach((p) => {
      const isActive = p.dataset.mode === mode;
      p.hidden = !isActive;
      p.querySelectorAll("input, select, textarea").forEach((el) => {
        el.disabled = !isActive;
      });
    });
    // Switching away from "new" hides any duplicate hint that may be open.
    if (mode !== "new") this._hideDup();
  }

  // ── Duplicate detection on the inline new-contact form ────────────────

  checkDup() {
    if (this._timer) clearTimeout(this._timer);
    if (!this.hasPrenomFieldTarget || !this.hasNomFieldTarget || !this.hasVerifyUrlValue) return;
    const prenom = this.prenomFieldTarget.value.trim();
    const nom    = this.nomFieldTarget.value.trim();
    if (prenom.length < 2 || nom.length < 2) { this._hideDup(); return; }
    this._timer = setTimeout(() => this._fetchDup(prenom, nom), 350);
  }

  _fetchDup(prenom, nom) {
    if (this._abort) this._abort.abort();
    this._abort = new AbortController();
    const url = `${this.verifyUrlValue}?prenom=${encodeURIComponent(prenom)}&nom=${encodeURIComponent(nom)}`;
    fetch(url, { headers: { Accept: "application/json" }, signal: this._abort.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => this._renderDup((p && p.matches) || []))
      .catch(() => {});
  }

  _renderDup(matches) {
    if (!this.hasDupTarget) return;
    if (!Array.isArray(matches) || matches.length === 0) {
      this._hideDup();
      return;
    }
    this.dupTitleTarget.textContent = matches.length === 1
      ? "Un contact très proche existe déjà :"
      : `${matches.length} contacts très proches existent déjà :`;
    this.dupListTarget.innerHTML = "";
    matches.forEach((m) => {
      const li = document.createElement("li");
      li.className = "csm_dup__item";
      const meta = [m.poste, m.entreprise].filter(Boolean).join(" · ");
      li.innerHTML = `
        <span class="csm_dup__name">${this._esc(m.prenom || "")} ${this._esc(m.nom || "")}</span>
        ${m.email ? `<span class="csm_dup__meta">${this._esc(m.email)}</span>` : ""}
        ${meta ? `<span class="csm_dup__meta">${this._esc(meta)}</span>` : ""}
        <span class="csm_dup__actions">
          <button type="button" class="csm_dup__use" data-id="${m.id}"
                  data-prenom="${this._esc(m.prenom || "")}"
                  data-nom="${this._esc(m.nom || "")}"
                  data-poste="${this._esc(m.poste || "")}">
            <i class="fa-solid fa-link"></i> Utiliser et rattacher
          </button>
          <a href="${this._esc(m.url)}" target="_blank" rel="noopener" class="csm_dup__view">
            <i class="fa-solid fa-arrow-up-right-from-square"></i> Voir
          </a>
        </span>
      `;
      li.querySelector(".csm_dup__use").addEventListener("click", (e) => {
        const btn = e.currentTarget;
        this._useExistingContact({
          id: btn.dataset.id,
          prenom: btn.dataset.prenom,
          nom: btn.dataset.nom,
          poste: btn.dataset.poste,
        });
      });
      this.dupListTarget.appendChild(li);
    });
    this.dupTarget.hidden = false;
  }

  _hideDup() {
    if (this.hasDupTarget) {
      this.dupTarget.hidden = true;
      this.dupListTarget.innerHTML = "";
    }
  }

  // ── "Utiliser ce contact" → switch to existing pane + preselect ───────
  // Server side: etudes#create does find_or_create_by on contact_entreprises,
  // so picking a contact from another entreprise here will silently rattacher
  // it to the étude's entreprise as a secondary employer.

  _useExistingContact({ id, prenom, nom, poste }) {
    // Flip the tab to "existing"
    const radio = this.tabTargets.find((t) => t.dataset.mode === "existing")
                    ?.querySelector("input[type=radio]");
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }

    // Inject the contact as an option if it's not already in the dropdown
    // (it usually isn't — the dropdown only carries contacts of the étude's
    // entreprise; a duplicate from another entreprise is a stranger here).
    if (this.hasExistingSelectTarget) {
      const $ = window.jQuery || window.$;
      const sel = this.existingSelectTarget;
      const label = `${prenom} ${nom}${poste ? ` — ${poste}` : ""}`;
      if (!Array.from(sel.options).some((o) => String(o.value) === String(id))) {
        sel.appendChild(new Option(label, id, true, true));
      }
      sel.value = id;
      if ($ && $.fn.select2) $(sel).trigger("change");
      else sel.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this._hideDup();
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
  }
}
